import * as React from "react";
import { WifiOff } from "lucide-react";
import { cn } from "@/lib/utils";

/* I quattro stati di ogni elenco: pieno, vuoto, in caricamento, in errore (pacchetto del 29-10).
   Ogni stato dice cosa è successo e cosa fare dopo. */
const toneBox = {
  success: "bg-success-bg text-success",
  brand: "bg-brand-subtle text-brand",
  neutral: "bg-surface-3 text-ink-2",
  danger: "bg-danger-bg text-danger",
  warning: "bg-warning-bg text-warning",
};

function Panel({ icon: Icon, tone = "neutral", title, children, actions, className, role }) {
  return (
    <div role={role} className={cn("flex animate-ida-rise flex-col items-center justify-center gap-3 p-6 text-center", className)}>
      <span className={cn("inline-flex size-14 items-center justify-center rounded-lg", toneBox[tone])} aria-hidden="true">
        <Icon className="size-7" strokeWidth={1.75} />
      </span>
      <h2 className="font-display text-h3 text-ink-strong">{title}</h2>
      {children && <div className="max-w-[540px] text-ink-2">{children}</div>}
      {actions && <div className="mt-1 flex flex-wrap justify-center gap-2 max-md:w-full max-md:flex-col">{actions}</div>}
    </div>
  );
}

/** Vuoto: perché è vuoto (in positivo se è una buona notizia) e l'azione per riempirlo. */
export function EmptyState(props) {
  return <Panel {...props} />;
}

/** Errore: cosa non va in parole semplici, «Riprova», e un codice per l'assistenza se il problema resta. */
export function ErrorState({ code, icon = WifiOff, tone = "danger", children, ...props }) {
  return (
    <Panel role="alert" icon={icon} tone={tone} {...props}>
      {children}
      {code && <span className="mt-1 block text-caption font-normal tabular-nums text-ink-3">Codice per l'assistenza: {code}</span>}
    </Panel>
  );
}

/** Caricamento: lo scheletro della forma vera, che pulsa piano. Dopo `slowAfterMs` una riga dice cosa succede. */
export function ListSkeleton({
  rows = 6,
  label = "Carico i dati…",
  slowText = "Ci sta mettendo più del solito. Se non arriva, controlla la rete.",
  slowAfterMs = 5000,
  className,
}) {
  const [slow, setSlow] = React.useState(false);
  React.useEffect(() => {
    const t = setTimeout(() => setSlow(true), slowAfterMs);
    return () => clearTimeout(t);
  }, [slowAfterMs]);
  const larghezze = [["52%", "88%"], ["64%", "76%"], ["40%", "92%"], ["58%", "70%"], ["48%", "84%"], ["60%", "80%"]];
  return (
    <div className={className}>
      <p role="status" className={slow ? "px-4 py-3 text-small text-ink-2" : "sr-only"}>
        {slow ? slowText : label}
      </p>
      <div aria-hidden="true">
        {Array.from({ length: rows }, (_, i) => {
          const [a, b] = larghezze[i % larghezze.length];
          const delay = { animationDelay: `${i * 80}ms` };
          return (
            <div key={i} className="flex gap-3 border-b border-line-subtle px-4 py-3.5">
              <span className="size-10 shrink-0 animate-ida-skeleton rounded-full bg-surface-3" style={delay} />
              <span className="flex flex-1 flex-col gap-2 pt-1">
                <span className="h-3 animate-ida-skeleton rounded-sm bg-surface-3" style={{ ...delay, width: a }} />
                <span className="h-2.5 animate-ida-skeleton rounded-sm bg-surface-3" style={{ ...delay, width: b }} />
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
